import React, { useEffect, useState } from 'react';
import type {
  InternalTrackingCode,
  ProratedRentErrors,
  ProratedRentRawParams,
  ProratedRentResult,
  TrackingCode,
} from './types';
import { TenantStatus } from './types';
import Form from './Form';
import Results from './Results';
import calculate from './calculate';
import validate from './validate';

// Constellation
import { FONT_TYPES, ThemeConstellation } from '@zillow/constellation';

// Misc / Utils
import * as S from './styles';
import { ThemeProvider } from 'styled-components';

enum CalculationState {
  INPUT_PARAMS,
  DISPLAY_RESULTS,
  ERRORED,
}

const TENANT_STATUSES = [TenantStatus.MOVE_IN, TenantStatus.MOVE_OUT];

interface ProratedRentCalculatorProps {
  itc: InternalTrackingCode;
  t: TrackingCode;
}

const ProratedRentCalculator: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<ProratedRentCalculatorProps>>
> = (props: ProratedRentCalculatorProps = { itc: '', t: '' }) => {
  const { itc, t } = props;
  const [state, setState] = useState<CalculationState>(CalculationState.INPUT_PARAMS);
  const [params, setParams] = useState<ProratedRentRawParams>({ tenantStatus: TENANT_STATUSES[0] });
  const clearParams = () => setParams({ tenantStatus: TENANT_STATUSES[0] });
  const [errors, setErrors] = useState<ProratedRentErrors>({});
  const clearErrors = () => setErrors({});
  const [proratedRent, setProratedRent] = useState<ProratedRentResult | undefined>();
  const handleCalculateButtonClick = () => {
    const validateResult = validate(params);
    if ('errors' in validateResult) {
      setErrors(validateResult.errors);
      setState(CalculationState.ERRORED);
    } else {
      clearErrors();
      const { tenantStatus, monthlyRent, moveDate } = params;
      setProratedRent(
        // typecasting is safe because params have passed validation
        calculate(tenantStatus as TenantStatus, parseFloat(monthlyRent as string), new Date(moveDate as string)),
      );
      setState(CalculationState.DISPLAY_RESULTS);
    }
  };

  const handleReset = () => {
    clearParams();
    setState(CalculationState.INPUT_PARAMS);
  };

  useEffect(() => {
    // continuously validate if user is in ERRORED state
    if (state === CalculationState.ERRORED) {
      const validationResult = validate(params);
      if ('errors' in validationResult) {
        setErrors(validationResult.errors);
      } else {
        clearErrors();
      }
    }
  }, [params, state]);

  return (
    <ThemeProvider theme={ThemeConstellation}>
      <S.Content cardType="secondary">
        <S.CenterText as="h1" fontType={FONT_TYPES.h4} marginBottom="sm">
          Prorated rent calculator
        </S.CenterText>
        <S.CenterText as="p" fontType={FONT_TYPES.body} marginBottom="md">
          Calculate your tenant&apos;s prorated rent.
        </S.CenterText>
        {(state === CalculationState.INPUT_PARAMS || state === CalculationState.ERRORED) && (
          <Form
            tenantStatuses={TENANT_STATUSES}
            setTenantStatus={(tenantStatus: TenantStatus) => setParams({ ...params, ...{ tenantStatus } })}
            setMonthlyRent={(monthlyRent: string) => setParams({ ...params, ...{ monthlyRent } })}
            setMoveDate={(moveDate: string) => setParams({ ...params, ...{ moveDate } })}
            handleCalculateButtonClick={handleCalculateButtonClick}
            errors={errors}
          />
        )}
        {state === CalculationState.DISPLAY_RESULTS && (
          <Results reset={handleReset} proratedRent={proratedRent as ProratedRentResult} itc={itc} t={t} />
        )}
      </S.Content>
    </ThemeProvider>
  );
};

export default ProratedRentCalculator;
